/**
 * useClassificacaoDocumento Hook - Manages Classificação Documento CRUD operations
 */
import { useState, useEffect, useCallback } from 'react';
import apiClient from '../services/apiClient';
import { toast } from 'react-hot-toast';
import { useConfirm } from './useConfirm';

interface ClassificacaoDocumento {
  id: number;
  classificacao: string;
  abreviacao: string;
}

interface NewClassificacao {
  classificacao: string;
  abreviacao: string;
}

interface UseClassificacaoDocumentoReturn {
  classificacoes: ClassificacaoDocumento[];
  isLoadingClassificacoes: boolean;
  isSavingClassificacao: boolean;
  editingClassificacao: number | null;
  newClassificacao: NewClassificacao;
  editClassificacaoData: NewClassificacao;
  setNewClassificacao: (classificacao: NewClassificacao) => void;
  setEditClassificacaoData: (classificacao: NewClassificacao) => void;
  handleCreateClassificacao: () => Promise<void>;
  handleUpdateClassificacao: (id: number) => Promise<void>;
  handleDeleteClassificacao: (id: number) => Promise<void>;
  startEditing: (classificacao: ClassificacaoDocumento) => void;
  cancelEditing: () => void;
  fetchClassificacoes: () => Promise<void>;
  ConfirmDialog: ReturnType<typeof useConfirm>['ConfirmDialog'];
}

export function useClassificacaoDocumento(activeSection: string): UseClassificacaoDocumentoReturn {
  const { confirm, ConfirmDialog } = useConfirm();

  const [classificacoes, setClassificacoes] = useState<ClassificacaoDocumento[]>([]);
  const [isLoadingClassificacoes, setIsLoadingClassificacoes] = useState(false);
  const [editingClassificacao, setEditingClassificacao] = useState<number | null>(null);
  const [newClassificacao, setNewClassificacao] = useState<NewClassificacao>({ classificacao: '', abreviacao: '' });
  const [editClassificacaoData, setEditClassificacaoData] = useState<NewClassificacao>({ classificacao: '', abreviacao: '' });
  const [isSavingClassificacao, setIsSavingClassificacao] = useState(false);

  const fetchClassificacoes = useCallback(async () => {
    setIsLoadingClassificacoes(true);
    try {
      const response = await apiClient.get('/config/classificacao-documento');
      setClassificacoes(response.data);
    } catch (error) {
      console.error('Error fetching classificações:', error);
      toast.error('Erro ao carregar lista de Classificações');
    } finally {
      setIsLoadingClassificacoes(false);
    }
  }, []);

  useEffect(() => {
    if (activeSection === 'classificacao-documento') {
      fetchClassificacoes();
    }
  }, [activeSection, fetchClassificacoes]);

  const handleCreateClassificacao = useCallback(async () => {
    if (!newClassificacao.classificacao.trim() || !newClassificacao.abreviacao.trim()) {
      toast.error('Preencha todos os campos');
      return;
    }

    setIsSavingClassificacao(true);
    try {
      const response = await apiClient.post('/config/classificacao-documento', newClassificacao);
      setClassificacoes(prev => [...prev, response.data]);
      setNewClassificacao({ classificacao: '', abreviacao: '' });
      toast.success('Classificação adicionada com sucesso');
    } catch (error: any) {
      console.error('Error creating classificação:', error);
      toast.error(error.response?.data?.detail || 'Erro ao adicionar classificação');
    } finally {
      setIsSavingClassificacao(false);
    }
  }, [newClassificacao]);

  const handleUpdateClassificacao = useCallback(async (id: number) => {
    if (!editClassificacaoData.classificacao.trim() || !editClassificacaoData.abreviacao.trim()) {
      toast.error('Preencha todos os campos');
      return;
    }

    setIsSavingClassificacao(true);
    try {
      const response = await apiClient.put(`/config/classificacao-documento/${id}`, editClassificacaoData);
      setClassificacoes(prev => prev.map(c => c.id === id ? response.data : c));
      setEditingClassificacao(null);
      toast.success('Classificação atualizada com sucesso');
    } catch (error: any) {
      console.error('Error updating classificação:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar classificação');
    } finally {
      setIsSavingClassificacao(false);
    }
  }, [editClassificacaoData]);

  const handleDeleteClassificacao = useCallback(async (id: number) => {
    const confirmed = await confirm({
      title: 'Excluir Classificação',
      message: 'Tem certeza que deseja excluir esta classificação?',
      variant: 'danger',
      confirmText: 'Excluir',
    });

    if (!confirmed) return;

    try {
      await apiClient.delete(`/config/classificacao-documento/${id}`);
      setClassificacoes(prev => prev.filter(c => c.id !== id));
      toast.success('Classificação excluída com sucesso');
    } catch (error: any) {
      console.error('Error deleting classificação:', error);
      toast.error('Erro ao excluir classificação');
    }
  }, [confirm]);

  const startEditing = useCallback((classificacao: ClassificacaoDocumento) => {
    setEditingClassificacao(classificacao.id);
    setEditClassificacaoData({ classificacao: classificacao.classificacao, abreviacao: classificacao.abreviacao });
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingClassificacao(null);
    setEditClassificacaoData({ classificacao: '', abreviacao: '' });
  }, []);

  return {
    classificacoes,
    isLoadingClassificacoes,
    isSavingClassificacao,
    editingClassificacao,
    newClassificacao,
    editClassificacaoData,
    setNewClassificacao,
    setEditClassificacaoData,
    handleCreateClassificacao,
    handleUpdateClassificacao,
    handleDeleteClassificacao,
    startEditing,
    cancelEditing,
    fetchClassificacoes,
    ConfirmDialog,
  };
}
